import type {
  AgentHandoff,
  ChatMessage,
  ChatSession,
  DataResponse,
  DocumentLineage,
  MessageAttachment,
  PaginatedResponse,
  SessionSharingResponse,
  SharedSessionSummary,
  ShareLinkResponse,
  SharePermission,
  SharePointDocumentMeta,
  ShareResponse,
} from "@/types";
import { api } from "../api";

export const chatService = {
  listSessions: (params?: {
    page?: number;
    pageSize?: number;
    starred?: boolean;
    tags?: string;
    projectUuid?: string;
    excludeProjectSessions?: boolean;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.pageSize) searchParams.set("page_size", String(params.pageSize));
    if (params?.starred !== undefined) searchParams.set("starred", String(params.starred));
    if (params?.tags) searchParams.set("tags", params.tags);
    if (params?.projectUuid) searchParams.set("project_uuid", params.projectUuid);
    if (params?.excludeProjectSessions) searchParams.set("exclude_project_sessions", "true");
    return api.get<PaginatedResponse<ChatSession>>(`/chat/sessions?${searchParams}`);
  },
  getSession: (sessionId: string) => api.get<DataResponse<ChatSession>>(`/chat/sessions/${sessionId}`),
  createSession: (data: {
    modelId: string;
    agentUuid?: string;
    brandUuid?: string;
    title?: string;
    projectUuid?: string;
    reasoningEffort?: string;
  }) => api.post<DataResponse<ChatSession>>("/chat/sessions", data),
  updateSession: (sessionId: string, data: { title?: string; isStarred?: boolean; reasoningEffort?: string | null }) =>
    api.patch<DataResponse<ChatSession>>(`/chat/sessions/${sessionId}`, data),
  deleteSession: (sessionId: string) => api.delete(`/chat/sessions/${sessionId}`),
  /** Move the chat to another agent mid-conversation, or take the router's suggestion (MHS-628). */
  handoff: (sessionId: string, data: { agentUuid: string; suggestionUuid?: string }) =>
    api.post<DataResponse<AgentHandoff>>(`/chat/sessions/${sessionId}/handoff`, data),
  getMessages: (sessionId: string) => api.get<DataResponse<ChatMessage[]>>(`/chat/sessions/${sessionId}/messages`),
  sendMessage: (sessionId: string, message: string, attachments?: File[]) => {
    if (attachments?.length) {
      return api.upload<DataResponse<ChatMessage>>(`/chat/sessions/${sessionId}/messages`, attachments, {
        content: message,
      });
    }
    return api.post<DataResponse<ChatMessage>>(`/chat/sessions/${sessionId}/messages`, {
      content: message,
    });
  },
  streamMessage: (
    sessionId: string,
    message: string,
    sharepointDocuments?: SharePointDocumentMeta[],
    sharepointContent?: string,
    sharepointSlideImages?: string[],
    imageAttachments?: string[],
    documentAttachments?: string[],
    disableTools?: boolean,
  ) =>
    api.post<{ operation_id: string }>(`/chat/sessions/${sessionId}/messages/stream`, {
      content: message,
      sharepointDocuments: sharepointDocuments ?? undefined,
      sharepointContent: sharepointContent ?? undefined,
      sharepointSlideImages: sharepointSlideImages?.length ? sharepointSlideImages : undefined,
      imageAttachments: imageAttachments?.length ? imageAttachments : undefined,
      documentAttachments: documentAttachments?.length ? documentAttachments : undefined,
      disableTools: disableTools ?? undefined,
    }),
  // Image attachments
  uploadAttachments: (sessionId: string, files: File[]) =>
    api.upload<DataResponse<MessageAttachment[]>>(`/chat/sessions/${sessionId}/attachments`, files),
  getAttachmentUrl: (attachmentUuid: string) =>
    api.get<DataResponse<{ url: string }>>(`/chat/attachments/${attachmentUuid}`),
  // Sharing
  getShares: (sessionId: string) =>
    api.get<DataResponse<SessionSharingResponse>>(`/chat/sessions/${sessionId}/sharing`),
  shareWithUser: (sessionId: string, userUuid: string, permission: SharePermission) =>
    api.post<DataResponse<ShareResponse>>(`/chat/sessions/${sessionId}/sharing/users`, {
      user_uuid: userUuid,
      permission,
    }),
  removeShare: (sessionId: string, userUuid: string) =>
    api.delete(`/chat/sessions/${sessionId}/sharing/users/${userUuid}`),
  createShareLink: (sessionId: string, permission: SharePermission) =>
    api.post<DataResponse<ShareLinkResponse>>(`/chat/sessions/${sessionId}/sharing/link`, {
      permission,
    }),
  removeShareLink: (sessionId: string, linkUuid: string) =>
    api.delete(`/chat/sessions/${sessionId}/sharing/link/${linkUuid}`),
  getSharedWithMe: () => api.get<DataResponse<SharedSessionSummary[]>>("/chat/shared-with-me"),
  accessShareLink: (token: string) => api.get<DataResponse<ChatSession>>(`/chat/shared/${token}`),
  forkSession: (sessionId: string) => api.post<DataResponse<ChatSession>>(`/chat/sessions/${sessionId}/fork`, {}),
  // Document lineage / version tracking
  getSessionDocuments: (sessionId: string) =>
    api.get<DataResponse<DocumentLineage[]>>(`/chat/sessions/${sessionId}/documents`),
};
